import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalyses } from '../hooks/useAnalysis';
import { useContacts, useSyncCrmContacts } from '../hooks/useContacts';
import { useLlmProviders } from '../hooks/useLlm';
import { useProjectIntegrations } from '../hooks/useProjectIntegrations';
import StatCard from '../components/ui/StatCard';
import ScorePill from '../components/ui/ScorePill';
import Bar from '../components/ui/Bar';
import Badge from '../components/ui/Badge';
import IntegrationGrid from '../components/IntegrationGrid';
import useProjectStore from '../stores/projectStore';
import { CRO_CATEGORIES } from '../lib/constants';
import { useProjectReports } from '../hooks/useProjectReports';

export default function Dashboard() {
  const navigate = useNavigate();
  const currentProjectId = useProjectStore((s) => s.currentProjectId);
  const currentProject = useProjectStore((s) => s.projects.find((p) => p.id === s.currentProjectId));
  const { data: analysesData } = useAnalyses();
  const { data: contactsData } = useContacts();
  const { data: llmData } = useLlmProviders();
  const { data: projectReports = [] } = useProjectReports();
  const { data: integrationsData } = useProjectIntegrations(currentProjectId);
  const latestReport = projectReports[0] || null;
  const syncCrm = useSyncCrmContacts();
  const hasSynced = useRef(false);

  // Auto-Sync: CRM-Kontakte beim ersten Dashboard-Laden abgleichen
  useEffect(() => {
    if (!hasSynced.current) {
      hasSynced.current = true;
      syncCrm.mutate();
    }
  }, []);

  const analyses = analysesData?.data || [];
  const contacts = contactsData?.data || [];
  const defaultProvider = llmData?.default || '—';

  const doneAnalyses = analyses.filter(a => a.status === 'done' && a.score);
  const avgScore = doneAnalyses.length > 0
    ? Math.round(doneAnalyses.reduce((s, a) => s + a.score, 0) / doneAnalyses.length)
    : '—';

  // Setup-Wizard-Logik: Wizard zeigen wenn (a) keine Integration gebucht oder (b) noch keine Analyse
  const integrations = integrationsData?.integrations || [];
  const bookedCount = integrations.filter(i => i.is_booked).length;
  const hasAnyIntegration = bookedCount > 0;
  const hasAnalyses = analyses.length > 0;
  const showWizard = !hasAnyIntegration && !hasAnalyses;
  const wizardStep = !hasAnalyses ? (!hasAnyIntegration ? 2 : 3) : null;

  return (
    <div>
      <div className="mb-5 flex items-start justify-between">
        <div>
          <h1 className="text-[23px] font-bold mb-0.5 flex items-center gap-3">
            {currentProject ? currentProject.name : 'Projekt'}
            {showWizard && (
              <span className="text-[10.5px] font-black tracking-[.14em] uppercase px-2.5 py-1 rounded-full bg-[#F5EEE0] text-[#A88A56]">
                Onboarding · Schritt {wizardStep} von 3
              </span>
            )}
          </h1>
          <p className="text-gray-500 text-[13.5px]">
            {currentProject?.domain ? (
              <span className="font-mono">{currentProject.domain} · </span>
            ) : null}
            {showWizard ? 'Ersteinrichtung' : 'CRO- und SEO-Aktivitäten dieses Projekts'}
          </p>
        </div>
        <button
          onClick={() => navigate('/overview')}
          className="text-[12px] text-teal font-medium hover:underline"
        >
          ← Global Overview
        </button>
      </div>

      {showWizard ? (
        <SetupWizard
          step={wizardStep}
          integrations={integrations}
          projectId={currentProjectId}
          onGoToSettings={() => navigate('/settings')}
          onStartAnalysis={() => navigate('/cro/analyses')}
        />
      ) : (
        <FullDashboard
          analyses={analyses}
          doneAnalyses={doneAnalyses}
          avgScore={avgScore}
          currentProject={currentProject}
          currentProjectId={currentProjectId}
          latestReport={latestReport}
          llmData={llmData}
          defaultProvider={defaultProvider}
          contacts={contacts}
          navigate={navigate}
        />
      )}
    </div>
  );
}

// ═══════════════════════ SETUP-WIZARD ═══════════════════════
function SetupWizard({ step, integrations, projectId, onGoToSettings, onStartAnalysis }) {
  const ot = integrations.find(i => i.type === 'cro_tracker') || {};
  const ga4 = integrations.find(i => i.type === 'ga4') || {};
  const gsc = integrations.find(i => i.type === 'gsc') || {};
  const xovi = integrations.find(i => i.type === 'xovi') || {};

  return (
    <div className="max-w-[900px] mx-auto mb-12">

      {/* Step-Progress */}
      <div className="flex items-center max-w-[520px] mx-auto mb-10">
        <StepDot state="done" label="Kunde angelegt" />
        <StepBar done />
        <StepDot state={step === 2 ? 'current' : 'done'} label="Anbindungen" />
        <StepBar done={step > 2} />
        <StepDot state={step === 3 ? 'current' : 'todo'} label="Erste Analyse" muted={step !== 3} />
      </div>

      {step === 2 && (
        <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-8 mb-6">
          <div className="text-center mb-7">
            <div className="inline-block text-[10.5px] font-black tracking-[.14em] uppercase text-[#A88A56] mb-3 bg-[#F5EEE0] px-3 py-1 rounded-full">Schritt 2 von 3</div>
            <h2 className="text-[26px] font-black tracking-tight mb-2">Welche Datenquellen sollen wir anschließen?</h2>
            <p className="text-gray-500 text-[14px] max-w-[560px] mx-auto">
              Wir empfehlen unseren <strong className="text-gray-900">OptimusAI-Tracker</strong> als Hauptdatenquelle — er ist kein Muss, aber liefert das tiefste Bild. GA4 und GSC ergänzen. Alles überspringbar, nachholbar in den Anbindungen.
            </p>
          </div>

          {/* Primär-Empfehlung: OptimusAI-Tracker */}
          <div className="relative mb-6">
            <div className="absolute -top-3 left-6 bg-[#C2A677] text-[#0E1626] text-[10px] font-black tracking-[.14em] uppercase px-3 py-1 rounded-full shadow-md">
              Unsere Empfehlung
            </div>
            <div className={`rounded-[14px] border-2 border-[#C2A677] bg-gradient-to-br from-[#F5EEE0]/50 to-white shadow-md p-6 ${ot.is_booked ? 'ring-2 ring-green-200' : ''}`}>
              <div className="grid grid-cols-[auto_1fr_auto] gap-5 items-start">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#C2A677] to-[#A88A56] grid place-items-center font-black text-white text-[15px]">OT</div>
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <div className="font-bold text-[17px]">OptimusAI-Tracker</div>
                    <StatusPill booked={!!ot.is_booked} label={ot.is_booked ? 'Aktiv' : 'Hauseigen · DSGVO · DE'} />
                  </div>
                  <div className="text-gray-500 text-[13px] mb-3">JS-Snippet einbinden · ca. 2 Min · cookie-frei, Events &amp; Verhalten tiefer als GA4.</div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    <UnlockLine>Session-Recordings &amp; Heatmaps</UnlockLine>
                    <UnlockLine>Cookie-frei-Tracking</UnlockLine>
                    <UnlockLine>Conversion-Analyse ohne GA4</UnlockLine>
                    <UnlockLine>A/B-Test-Uplift direkt messbar</UnlockLine>
                  </div>
                </div>
                <div className="flex flex-col gap-2 items-stretch w-[150px]">
                  <button
                    onClick={onGoToSettings}
                    className="bg-[#C2A677] text-[#0E1626] hover:bg-[#A88A56] hover:text-white text-[12.5px] font-bold py-2 px-3 rounded-md"
                  >
                    {ot.is_booked ? 'Konfigurieren' : 'Snippet anzeigen →'}
                  </button>
                  {!ot.is_booked && (
                    <button
                      onClick={onGoToSettings}
                      className="text-gray-500 text-[11.5px] font-semibold hover:text-gray-900"
                    >
                      Überspringen
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="text-[11px] text-gray-500 uppercase tracking-widest font-bold mb-3 mt-7">
            Zusätzliche Quellen · optional
          </div>

          <div className="grid grid-cols-2 gap-3 mb-2">
            <MiniIntegCard
              code="G4"
              bg="#FFF4E0"
              color="#D97706"
              title="Google Analytics 4"
              sub="OAuth · ergänzt historische Daten"
              unlocks={['Historische Sessions & Audience', 'Validierung gegen Tracker']}
              booked={!!ga4.is_booked}
              onConnect={onGoToSettings}
            />
            <MiniIntegCard
              code="GS"
              bg="#E0F2FE"
              color="#0369A1"
              title="Google Search Console"
              sub="OAuth · SEO-Modul freischalten"
              unlocks={['SEO-Rankings & Keywords', 'Wöchentlicher SEO-Report']}
              booked={!!gsc.is_booked}
              onConnect={onGoToSettings}
            />
            <MiniIntegCard
              code="XO"
              bg="#FEE2E2"
              color="#B91C1C"
              title="XOVI SEO-Suite"
              sub="API-Key · Wettbewerbs-Daten"
              unlocks={['Wettbewerbs-Rankings', 'Backlink-Analyse']}
              booked={!!xovi.is_booked}
              onConnect={onGoToSettings}
              optional
            />
            <MiniIntegCard
              code="GA"
              bg="#FEF3C7"
              color="#92400E"
              title="Google Ads"
              sub="Nur bei bezahlten Kampagnen"
              unlocks={['Kampagnen-Performance', 'Budget-Tracking']}
              booked={false}
              onConnect={onGoToSettings}
              optional
            />
          </div>

          {/* Info-Hinweis */}
          <div className="mt-6 p-4 rounded-lg bg-[#F5EEE0]/40 border border-[#C2A677]/20 flex items-start gap-3">
            <div className="w-7 h-7 rounded-lg bg-[#C2A677] grid place-items-center text-white font-black flex-shrink-0 text-[12px]">i</div>
            <div className="flex-1">
              <div className="font-semibold text-[13.5px] mb-0.5">Keine Anbindung nötig</div>
              <div className="text-[12.5px] text-gray-600">
                Die Conversion-Analyse prüft Screenshot, HTML und Core Web Vitals — <strong className="text-gray-900">301 von 304 Faktoren</strong> laufen rein heuristisch. OptimusAI-Tracker und GA4 vertiefen später um echtes Nutzerverhalten, sind aber nie Voraussetzung.
              </div>
            </div>
          </div>

          {/* Wizard Nav */}
          <div className="flex items-center justify-between mt-7 pt-6 border-t border-gray-200">
            <div className="text-[12.5px] text-gray-500">
              {integrations.filter(i => i.is_booked).length} von {integrations.length || 4} Anbindungen aktiv
            </div>
            <button
              onClick={onStartAnalysis}
              className="bg-[#C2A677] text-[#0E1626] hover:bg-[#A88A56] hover:text-white text-[13px] font-bold py-2.5 px-5 rounded-md"
            >
              {integrations.filter(i => i.is_booked).length === 0 ? 'Sichtanalyse ohne Anbindung starten →' : 'Weiter: Erste Analyse →'}
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-8 mb-6 text-center">
          <div className="inline-block text-[10.5px] font-black tracking-[.14em] uppercase text-[#A88A56] mb-3 bg-[#F5EEE0] px-3 py-1 rounded-full">Schritt 3 von 3</div>
          <h2 className="text-[26px] font-black tracking-tight mb-2">Zeit für die erste Conversion-Analyse</h2>
          <p className="text-gray-500 text-[14px] max-w-[520px] mx-auto mb-6">
            Anbindungen stehen. Jetzt lassen wir den ersten Check laufen: Wir analysieren eine URL auf {integrations.filter(i => i.is_booked).length} Datenquellen und identifizieren die wichtigsten Hebel.
          </p>

          <div className="grid grid-cols-3 gap-3 mb-7 text-left max-w-[640px] mx-auto">
            <FeaturePreview title="~5 Min Laufzeit" sub="Automatisiert im Hintergrund" />
            <FeaturePreview title="304 Faktoren geprüft" sub="Verhaltensökonomisch fundiert" />
            <FeaturePreview title="Priorisiert nach Impact" sub="Was bringt am meisten CR?" />
          </div>

          <button
            onClick={onStartAnalysis}
            className="bg-[#C2A677] text-[#0E1626] hover:bg-[#A88A56] hover:text-white text-[14px] font-bold py-3 px-8 rounded-md"
          >
            Jetzt erste Analyse starten →
          </button>
          <div className="mt-3">
            <button
              onClick={onGoToSettings}
              className="text-gray-500 text-[12px] hover:text-gray-900"
            >
              ← Anbindungen ergänzen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Helper-Komponenten ────────────────────────────────────
function StepDot({ state, label, muted }) {
  const bg = state === 'done' ? 'bg-[#C2A677] text-white'
    : state === 'current' ? 'bg-[#0E1626] text-white ring-4 ring-[#C2A677]/25'
    : 'bg-gray-200 text-gray-500';
  return (
    <div className="flex flex-col items-center gap-2">
      <div className={`w-7 h-7 rounded-full grid place-items-center font-bold text-[12px] ${bg}`}>
        {state === 'done' ? '✓' : state === 'current' ? (label === 'Anbindungen' ? '2' : '3') : (label === 'Erste Analyse' ? '3' : '2')}
      </div>
      <div className={`text-[11.5px] font-semibold ${state === 'done' ? 'text-green-700' : muted ? 'text-gray-400' : 'text-gray-900'}`}>
        {label}
      </div>
    </div>
  );
}

function StepBar({ done }) {
  return <div className={`h-0.5 flex-1 mx-2 rounded ${done ? 'bg-[#C2A677]' : 'bg-gray-200'}`} />;
}

function UnlockLine({ children }) {
  return (
    <div className="text-[12px] text-gray-700 flex items-center gap-2 py-1">
      <span className="w-[5px] h-px bg-[#C2A677] flex-shrink-0 inline-block" />
      {children}
    </div>
  );
}

function StatusPill({ booked, label }) {
  if (booked) {
    return <span className="text-[10.5px] font-bold uppercase tracking-wider bg-green-50 text-green-700 px-2 py-0.5 rounded-full">Verbunden</span>;
  }
  return <span className="text-[10.5px] font-bold uppercase tracking-wider bg-green-50 text-green-700 px-2 py-0.5 rounded-full">{label}</span>;
}

function MiniIntegCard({ code, bg, color, title, sub, unlocks, booked, onConnect, optional }) {
  return (
    <div className={`rounded-[14px] border border-gray-200 p-5 ${booked ? 'bg-green-50/30 border-green-200' : 'bg-white'}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-lg grid place-items-center font-bold" style={{ background: bg, color }}>
          {code}
        </div>
        <span className={`text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${booked ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
          {booked ? 'Verbunden' : (optional ? 'Optional' : 'Nicht verbunden')}
        </span>
      </div>
      <div className="font-bold text-[14px] mb-1">{title}</div>
      <div className="text-gray-500 text-[12px] mb-3">{sub}</div>
      {unlocks.map((u, i) => <UnlockLine key={i}>{u}</UnlockLine>)}
      <button
        onClick={onConnect}
        className={`mt-3 text-[12px] py-1.5 px-3 w-full font-semibold rounded-md ${
          optional
            ? 'border border-gray-200 text-gray-600 hover:bg-gray-50'
            : booked
              ? 'border border-gray-200 text-gray-700 hover:bg-gray-50'
              : 'bg-[#0E1626] text-white hover:bg-[#1E293B]'
        }`}
      >
        {booked ? 'Konfigurieren' : (optional ? 'Später' : 'Verbinden')}
      </button>
    </div>
  );
}

function FeaturePreview({ title, sub }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
      <div className="font-semibold text-[13px] mb-1">{title}</div>
      <div className="text-[11.5px] text-gray-500">{sub}</div>
    </div>
  );
}

// ═══════════════════════ FULL DASHBOARD (nach Onboarding) ═══════════════════════
function FullDashboard({ analyses, doneAnalyses, avgScore, currentProject, currentProjectId, latestReport, llmData, defaultProvider, contacts, navigate }) {
  return (
    <>
      <div className="flex gap-3.5 mb-5 flex-wrap">
        <StatCard label="CRO-Analysen" value={analyses.length || '—'} sub={`${doneAnalyses.length} abgeschlossen`} icon="⟳" iconBg="#DBEAFE" iconColor="#3B82F6" />
        <StatCard label="Ø CRO-Score" value={avgScore} sub={doneAnalyses.length ? `aus ${doneAnalyses.length} Analysen` : 'keine Scores'} icon="◈" iconBg="#D1FAE5" iconColor="#10B981" />
        <StatCard label="SEO Keywords" value={currentProject?.seo_keywords_count || '—'} sub="getrackt" icon="◎" iconBg="#FEF3C7" iconColor="#B45309" />
        <StatCard label="Maßnahmen" value={currentProject?.seo_measures_count || '—'} sub="offen + erledigt" icon="☑" iconBg="#EDE9FE" iconColor="#8B5CF6" />
      </div>

      <div className="mb-4">
        <IntegrationGrid projectId={currentProjectId} />
      </div>

      <MonthlyReportCard report={latestReport} onOpen={() => navigate('/project-reports')} />

      <div className="grid grid-cols-[1fr_1fr_290px] gap-4">
        {/* Letzte Analysen */}
        <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-[14.5px]">Letzte Analysen</h3>
            <button onClick={() => navigate('/cro/analyses')} className="text-[13px] px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50">
              Alle →
            </button>
          </div>
          {analyses.length === 0 ? (
            <p className="text-gray-400 text-sm py-8 text-center">Noch keine Analysen vorhanden.</p>
          ) : (
            analyses.slice(0, 4).map((a) => (
              <div key={a.id} className="flex items-center gap-3 py-2.5 border-b border-gray-200 last:border-0">
                <ScorePill score={a.score || 0} />
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-medium truncate">{a.url}</div>
                  <div className="text-[11px] text-gray-500">
                    {a.contact?.name ? `${a.contact.name} · ` : ''}{a.page_type} · {new Date(a.created_at).toLocaleDateString('de')}
                  </div>
                </div>
                {a.provider && <Badge bg="#F3F4F6" color="#6B7280">{a.provider}</Badge>}
              </div>
            ))
          )}
          <button onClick={() => navigate('/cro/analyses')} className="w-full mt-3.5 bg-teal text-white py-2.5 rounded-lg font-medium text-[13px] hover:opacity-90">
            + Neue Analyse
          </button>
        </div>

        {/* Score nach Kategorie */}
        <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
          <h3 className="font-semibold text-[14.5px] mb-4">Score nach Kategorie</h3>
          {CRO_CATEGORIES.slice(0, 6).map((c) => (
            <div key={c.key} className="mb-3">
              <div className="flex justify-between text-[12px] mb-1">
                <span className="font-medium">{c.name}</span>
                <span style={{ color: c.color }} className="font-semibold">—</span>
              </div>
              <Bar pct={0} color={c.color} />
            </div>
          ))}
        </div>

        {/* Rechte Spalte */}
        <div className="flex flex-col gap-3.5">
          {/* LLM Provider Status */}
          <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
            <h3 className="font-semibold text-[14.5px] mb-3">LLM Provider</h3>
            <div className="flex items-center gap-2 px-3 py-2.5 bg-teal-light rounded-[7px] mb-2.5">
              <div className="w-[7px] h-[7px] rounded-full bg-teal" />
              <span className="text-[12px] text-teal font-medium">
                Standard: {defaultProvider === 'claude' ? 'Claude' : defaultProvider === 'openai' ? 'OpenAI' : defaultProvider}
              </span>
            </div>
            {(llmData?.providers || []).map((p) => (
              <div key={p.name} className="flex items-center justify-between text-[12px] py-1">
                <span className="text-gray-600">{p.name === 'claude' ? 'Claude' : p.name === 'openai' ? 'OpenAI' : 'Self-Hosted'}</span>
                <Badge bg={p.available ? '#D1FAE5' : '#F3F4F6'} color={p.available ? '#10B981' : '#6B7280'}>
                  {p.available ? 'Aktiv' : 'Inaktiv'}
                </Badge>
              </div>
            ))}
          </div>

          {/* Letzte Kunden */}
          <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-[14.5px]">Kunden</h3>
              <button onClick={() => navigate('/contacts')} className="text-[12px] text-teal font-medium">Alle →</button>
            </div>
            {contacts.length === 0 ? (
              <p className="text-gray-400 text-[12px] py-3 text-center">Keine Kunden</p>
            ) : (
              contacts.slice(0, 4).map((c) => (
                <div
                  key={c.id}
                  onClick={() => navigate(`/contacts/${c.id}`)}
                  className="flex items-center justify-between py-1.5 cursor-pointer hover:text-teal text-[12px]"
                >
                  <span className="font-medium">{c.company || c.name}</span>
                  <span className="text-gray-400">{c.analyses_count || 0} Analysen</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function MonthlyReportCard({ report, onOpen }) {
  if (!report) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5 mb-4 flex items-center justify-between">
        <div>
          <div className="text-[11px] font-semibold text-gray-500 tracking-wider mb-1">MONATSREPORT</div>
          <div className="text-[14px] font-semibold">Noch kein Monatsreport für dieses Projekt</div>
          <div className="text-[12px] text-gray-500 mt-0.5">
            Erstelle einen Snapshot-Report über CRO, SEO und Maßnahmen — ideal als Kundenreporting.
          </div>
        </div>
        <button
          onClick={onOpen}
          className="px-4 py-2 bg-teal text-white rounded-lg text-[13px] font-medium hover:opacity-90"
        >
          Report erstellen →
        </button>
      </div>
    );
  }

  const cro = report.data?.cro || {};
  const seo = report.data?.seo || {};
  const measures = report.data?.measures || {};
  const period = report.data?.period?.label || '—';

  const winners = (seo.top_winners || []).filter((k) => (k.change ?? 0) > 0).length;
  const losers = (seo.top_losers || []).filter((k) => (k.change ?? 0) < 0).length;
  const completedMeasures = (measures.completed_in_period || []).length;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5 mb-4">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[11px] font-semibold text-gray-500 tracking-wider mb-1">MONATSREPORT</div>
          <div className="text-[15px] font-bold">{report.title}</div>
          <div className="text-[11.5px] text-gray-500 mt-0.5">
            {period} · erstellt {new Date(report.created_at).toLocaleDateString('de')}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => window.open(`/api/project-reports/${report.id}/pdf`, '_blank')}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-[12px] hover:bg-gray-50"
          >
            PDF
          </button>
          <button
            onClick={onOpen}
            className="px-3 py-1.5 bg-teal text-white rounded-lg text-[12px] font-medium hover:opacity-90"
          >
            Öffnen →
          </button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <MiniStat
          label="CRO-Analysen"
          value={cro.analyses_count ?? 0}
          sub={cro.avg_score != null ? `Ø Score ${cro.avg_score}` : 'keine Scores'}
          color="#0C9488"
          bg="#E6F4F2"
        />
        <MiniStat
          label="Keywords tracked"
          value={seo.keywords_count ?? 0}
          sub={seo.avg_position != null ? `Ø Pos. ${seo.avg_position}` : 'keine Positionen'}
          color="#3B82F6"
          bg="#DBEAFE"
        />
        <MiniStat
          label="Ranking-Deltas"
          value={`+${winners} / −${losers}`}
          sub={`${seo.on_target_count ?? 0} im Zielkorridor`}
          color="#10B981"
          bg="#D1FAE5"
        />
        <MiniStat
          label="Maßnahmen erledigt"
          value={completedMeasures}
          sub={`${measures.total ?? 0} gesamt`}
          color="#8B5CF6"
          bg="#EDE9FE"
        />
      </div>
    </div>
  );
}

function MiniStat({ label, value, sub, color, bg }) {
  return (
    <div className="rounded-lg p-3" style={{ background: bg }}>
      <div className="text-[10.5px] font-semibold tracking-wider" style={{ color }}>
        {label.toUpperCase()}
      </div>
      <div className="text-[20px] font-bold mt-0.5" style={{ color }}>
        {value}
      </div>
      <div className="text-[11px] text-gray-600 mt-0.5">{sub}</div>
    </div>
  );
}
